import React, { PropsWithChildren, ReactElement, ReactNode } from "react";
import Modal, { ModalProps } from "react-bootstrap/Modal";
// Modal.Header's typing can be overly strict depending on react-bootstrap/@types/react versions.
// Use a local any-typed alias here to avoid JSX prop type mismatches while keeping runtime behavior.
const ModalHeader: any = (Modal as any).Header;
import ImpactXButton from "../impactx-button";
import "./index.scss";
import _ from "lodash";
import castTo from "../../../utils/cast-to";

type Props = {
  dialogTitle?: string;
  headerContent?: ReactNode;
  subTitle?: string;
  highlightSubTitle?: boolean;
  bodyText?: string | ReactElement;
  bodyIcon?: ReactNode;
  show: boolean;
  onHide: () => void;
  dontAutoHideOnButtonClick?: boolean;
  dialogClassName?: string;
  modalSize?: ModalProps["size"];
  showCloseButton?: boolean;
  footerButtonAlignment?: "left" | "center";
  primaryButton?: {
    show: boolean;
    onClick?: () => void;
    label?: string;
    disabled?: boolean;
  };
  secondaryButton?: {
    show: boolean;
    onClick?: () => void;
    label?: string;
    disabled?: boolean;
  };
  footerContent?: ReactNode;
};

const defaultProps = {
  secondaryButton: {
    show: false,
    label: "No",
  },
  primaryButton: {
    show: false,
    label: "Yes",
  },
  bodyText: "",
  dontAutoHideOnButtonClick: false,
  showCloseButton: true,
  footerButtonAlignment: "left",
};

const NeoModalDialog = (props: PropsWithChildren<Props>) => {
  const allProps = castTo<PropsWithChildren<Props>>(_.defaultsDeep({ ...props }, defaultProps));

  const onClickHandler = (buttonProps: Props["primaryButton"] | Props["secondaryButton"]) => () => {
    if (!allProps.dontAutoHideOnButtonClick) {
      allProps.onHide();
    }
    buttonProps?.onClick?.();
  };

  const renderFooterButton = (
    buttonProps: Props["primaryButton"] | Props["secondaryButton"],
    isButtonStyleOutline: boolean,
  ) => {
    if (buttonProps?.show) {
      return (
        <ImpactXButton
          className="modal-button"
          outline={isButtonStyleOutline}
          label={buttonProps.label}
          onClickHandler={onClickHandler(buttonProps)}
          disabled={buttonProps.disabled ?? false}
        />
      );
    }
    return null;
  };

  const displayFooterContent = () => {
    if (props.footerContent) {
      return props.footerContent;
    }
    if (allProps.secondaryButton?.show || allProps.primaryButton?.show) {
      return (
        <div
          className="footer"
          style={{ justifyContent: allProps.footerButtonAlignment === "center" ? "center" : "flex-start" }}
        >
          {renderFooterButton(allProps.secondaryButton, true)}
          {renderFooterButton(allProps.primaryButton, false)}
        </div>
      );
    }
    return null;
  };

  const renderModalBody = () => {
    if (allProps.children) {
      return allProps.children;
    }
    else {
      return (
        <div className="dialogue-body">
          {allProps.bodyIcon}
          {allProps.bodyText}
        </div>
      );
    }
  };

  const renderSubTitle = () => {
    if (allProps.subTitle) {
      return (
        <div className={`dialogue-sub-title ${props.highlightSubTitle ? "dialogue-sub-title-highlighted" : ""}`}>
          <b>{allProps.subTitle}</b>
        </div>
      );
    }
  };

  const renderModalHeader = () => props.headerContent ?? <h3>{allProps.dialogTitle}</h3>;

  return (
    <>
      <Modal
        show={allProps.show}
        onHide={allProps.onHide}
        backdrop="static"
        dialogClassName={`${allProps.dialogClassName ?? ""} NeoModalDialog`}
        centered
        size={allProps.modalSize}
      >
        <ModalHeader
          closeButton={allProps.showCloseButton}
          className="header"
        >
          {renderModalHeader()}
        </ModalHeader>
        {renderSubTitle()}
        {renderModalBody()}
        {displayFooterContent()}
      </Modal>
    </>
  );
};

export default NeoModalDialog;
